import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Button,
  Text,
  Heading,
  Stack,
  Frame,
  Badge,
  Icon,
  ButtonGroup,
  Modal,
  TextContainer,
  Banner,
  SkeletonBodyText,
  SkeletonDisplayText,
  TextStyle,
} from '@shopify/polaris';
import { CircleTickMajor, StarFilledMinor, StarOutlineMinor, InfoMinor } from '@shopify/polaris-icons';
import LandingNav from './components/LandingNav';
import { getLandingPageSubscriptionPlans, getLandingPageFlexCreditPackages } from '../../services/landingPageService';

const Pricing = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeSales, setActiveSales] = useState({
    subscription: { active: false, name: '', percentage: 0 },
    flexCredits: { active: false, name: '', percentage: 0 }
  });
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const yearlyDiscountPercentage = 20; // 20% discount for yearly plans
  const [explanationModalOpen, setExplanationModalOpen] = useState(false);
  const [currentExplanation, setCurrentExplanation] = useState('');
  
  const handleNavClick = (path) => {
    navigate(path);
  };
  
  const handleLoginClick = () => {
    navigate('/auth');
  };

  // Helper function to convert markdown-style asterisks to HTML bold tags
  const convertMarkdownToBold = (text) => {
    if (!text) return '';
    // Replace **text** with <strong>text</strong>
    return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  };

  // Function to calculate yearly price with discount
  const calculateYearlyPrice = (monthlyPrice) => {
    const yearlyPrice = monthlyPrice * 12 * (1 - yearlyDiscountPercentage / 100);
    return yearlyPrice.toFixed(2);
  };

  // Function to round a price to .99
  const roundTo99 = (price) => {
    // Convert to number, floor it, and add 0.99
    return (Math.floor(parseFloat(price)) + 0.99).toFixed(2);
  };

  // Function to get the appropriate price based on billing period
  const getPriceDisplay = (plan) => {
    if (billingPeriod === 'yearly') {
      return calculateYearlyPrice(plan.price);
    }
    return plan.price.toFixed(2);
  };

  // Helper function to render star icons based on plan level
  const renderStars = (planLevel, planId) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Icon key={`${planId}-star-${i}`} source={i <= planLevel ? StarFilledMinor : StarOutlineMinor} color={i <= planLevel ? 'success' : 'subdued'} />
      );
    }
    return <Stack spacing="extraTight">{stars}</Stack>;
  };

  // Helper function to render star icons for all package displays
  const renderStarsForPackage = (packageNumber) => {
    const filled = Math.min(packageNumber, 5);
    const stars = [];
    
    for (let i = 1; i <= 5; i++) {
      const isFilled = i <= filled;
      stars.push(
        <Icon 
          key={`package-star-${i}`} 
          source={isFilled ? StarFilledMinor : StarOutlineMinor} 
          color={isFilled ? 'success' : 'subdued'} 
        />
      );
    }
    
    return <Stack spacing="extraTight">{stars}</Stack>;
  };

  // Fetch subscription plans and flex credit packages
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Use the landing page service which uses anonymous authentication
        const { plans: plansData, activeSales: salesData } = await getLandingPageSubscriptionPlans();
        const { packages: packagesData, activeSales: flexCreditSalesData } = await getLandingPageFlexCreditPackages();
        
        // Sort plans by sortOrder
        const sortedPlans = Object.fromEntries(
          Object.entries(plansData)
            .filter(([_, plan]) => plan.enabled)
            .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
        );
        
        // Sort packages by sortOrder
        const sortedPackages = Object.fromEntries(
          Object.entries(packagesData)
            .filter(([_, pack]) => pack.enabled)
            .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
        );
        
        setPlans(sortedPlans);
        setPackages(sortedPackages);
        
        // Set active sales data
        setActiveSales({
          subscription: salesData?.subscription || { active: false, name: '', percentage: 0 },
          flexCredits: flexCreditSalesData?.credits || { active: false, name: '', percentage: 0 }
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Function to format explanation text with line breaks and bullet points for the modal
  const formatExplanationText = (content) => {
    if (!content) return null;
    
    // First, process it as markdown with bold formatting
    let processedContent = convertMarkdownToBold(content);
    
    // Replace line breaks with JSX <br/> elements
    const lines = processedContent.split('\n');
    return (
      <>
        {lines.map((line, index) => {
          // Check if the line starts with a bullet point
          if (line.startsWith('•')) {
            return (
              <div key={index} style={{ display: 'flex', marginBottom: '8px' }}>
                <span style={{ marginRight: '8px' }}>•</span>
                <span dangerouslySetInnerHTML={{ __html: line.substring(1) }} />
              </div>
            );
          } else {
            return (
              <div key={index} style={{ marginBottom: '8px' }}>
                <span dangerouslySetInnerHTML={{ __html: line }} />
              </div>
            );
          }
        })}
      </>
    );
  };

  // Show explanation modal when info icon is clicked
  const handleShowExplanation = (explanation) => {
    setCurrentExplanation(explanation);
    setExplanationModalOpen(true);
  };

  // Render feature with checkmark
  const renderFeature = (feature, icon = null) => {
    return (
      <Stack alignment="center" spacing="tight">
        <div style={{ color: '#008060' }}>
          <Icon source={CircleTickMajor} />
        </div>
        {icon && <div style={{ marginRight: '4px' }}>{icon}</div>}
        <Text variant="bodyMd" as="span">
          <span dangerouslySetInnerHTML={{ __html: convertMarkdownToBold(feature) }} />
        </Text>
      </Stack>
    );
  };

  // Explanation modal for when user clicks info icons
  const renderExplanationModal = (
    <Modal
      open={explanationModalOpen}
      onClose={() => setExplanationModalOpen(false)}
      title="Information"
      primaryAction={{
        content: 'Close',
        onAction: () => setExplanationModalOpen(false),
      }}
    >
      <Modal.Section>
        <TextContainer>
          {formatExplanationText(currentExplanation)}
        </TextContainer>
      </Modal.Section>
    </Modal>
  );

  return (
    <Frame>
      <Page>
        <LandingNav currentPath="/pricing" onNavClick={handleNavClick} />
        
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #f5f9ff 0%, #eefbf4 100%)',
          padding: '40px 20px',
          borderRadius: '8px',
          marginBottom: '40px',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Heading element="h1">
              <span style={{ fontSize: '2.2rem', fontWeight: '700', color: '#212B36' }}>
                Editify Pricing
              </span>
            </Heading>
            <div style={{ margin: '20px 0', fontSize: '1.1rem', color: '#637381' }}>
              <p>Choose the plan that best fits your Shopify business needs with no hidden fees</p>
            </div>
          </div>
        </div>

        {/* Billing Toggle */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          marginBottom: '30px',
          marginTop: '10px'
        }}>
          <div style={{
            padding: '8px 12px',
            background: '#f4f6f8',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <ButtonGroup segmented>
              <Button
                pressed={billingPeriod === 'monthly'}
                onClick={() => setBillingPeriod('monthly')}
              >
                Monthly Billing
              </Button>
              <Button
                pressed={billingPeriod === 'yearly'}
                onClick={() => setBillingPeriod('yearly')}
              >
                Yearly Billing (Save {yearlyDiscountPercentage}%)
              </Button>
            </ButtonGroup>
          </div>
        </div>
        
        {/* Active Sale Banner for Subscriptions */}
        {activeSales.subscription.active && (
          <div style={{ marginBottom: '30px' }}>
            <Banner status="success" title={activeSales.subscription.name || 'Limited Time Offer'}>
              <p>Save {activeSales.subscription.percentage}% on all subscription plans!</p>
            </Banner>
          </div>
        )}

        {/* Subscription Plans Section */}
        <Heading>Subscription Plans</Heading>
        <Text variant="bodyLg" as="p" color="subdued" style={{ marginBottom: '25px' }}>
          Choose the perfect Editify plan for your Shopify store
        </Text>
        
        <Layout>
          {loading ? (
            Array(3).fill().map((_, index) => (
              <Layout.Section oneThird key={`skeleton-${index}`}>
                <Card sectioned>
                  <SkeletonDisplayText size="small" />
                  <div style={{ height: '20px' }} />
                  <SkeletonDisplayText size="large" />
                  <div style={{ height: '20px' }} />
                  <SkeletonBodyText lines={4} />
                </Card>
              </Layout.Section>
            ))
          ) : (
            Object.entries(plans).length === 0 ? (
              <Layout.Section>
                <Banner status="warning">
                  <p>No subscription plans available. Please check back later.</p>
                </Banner>
              </Layout.Section>
            ) : (
              Object.entries(plans).map(([planId, plan]) => {
                // Only mark plan_3 as most popular, ignore any mostPopular property in the data
                const isMostPopular = planId === 'plan_3';
                const price = getPriceDisplay(plan);
                const savingsText = billingPeriod === 'yearly' ? `Save ${yearlyDiscountPercentage}%` : '';

                // Use compareAtPrice from the plan object if available, otherwise use null
                // This ensures consistency with the SubscriptionPlans.js approach
                let originalPrice = null;
                if (plan.compareAtPrice && activeSales.subscription.active) {
                  if (billingPeriod === 'yearly') {
                    // For yearly, we need to apply the yearly discount to the compareAtPrice
                    const yearlyCompareAtPrice = calculateYearlyPrice(plan.compareAtPrice);
                    originalPrice = roundTo99(yearlyCompareAtPrice);
                  } else {
                    // For monthly, use the compareAtPrice directly with rounding
                    originalPrice = roundTo99(plan.compareAtPrice.toString());
                  }
                }

                // Find the plan with the most features to determine max height
                const maxFeatures = Math.max(...Object.values(plans).map(plan => plan.features ? plan.features.length : 0));
                
                // Ensure all plans have the same feature list height by padding with empty space if needed
                const paddedFeatures = [...(plan.features || [])];
                while (paddedFeatures.length < maxFeatures) {
                  paddedFeatures.push('');
                }

                return (
                  <Layout.Section oneThird key={planId}>
                    <div style={{ height: '100%' }}>
                      <Card
                        sectioned
                        style={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          position: 'relative',
                          border: isMostPopular ? '2px solid #008060' : '1px solid #dfe3e8',
                          boxShadow: isMostPopular ? '0 0 10px rgba(0, 128, 96, 0.3)' : 'none',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          background: isMostPopular ? 'linear-gradient(135deg, rgba(0, 128, 96, 0.05) 0%, rgba(255, 255, 255, 1) 100%)' : 'white',
                          transform: isMostPopular ? 'translateY(-10px)' : 'none',
                        }}
                        onMouseEnter={(e) => {
                          if (!isMostPopular) {
                            e.currentTarget.style.transform = 'translateY(-5px)';
                            e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 128, 96, 0.15)';
                          } else {
                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 128, 96, 0.25)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isMostPopular) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          } else {
                            e.currentTarget.style.transform = 'translateY(-10px)';
                            e.currentTarget.style.boxShadow = '0 0 10px rgba(0, 128, 96, 0.3)';
                          }
                        }}
                      >
                        <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                          <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                            {renderStarsForPackage(parseInt(planId.split('_')[1]) || 1)}
                          </div>
                          <Heading element="h2">{plan.name}</Heading>
                          {/* Badge container with fixed height for all plans */}
                          <div style={{ marginTop: '5px', height: '24px' }}>
                            {planId === 'plan_3' && (
                              <Badge status="success">MOST POPULAR</Badge>
                            )}
                          </div>
                          <div style={{ marginTop: '10px', color: '#637381', fontSize: '14px', minHeight: '40px' }}>
                            {plan.description}
                          </div>
                        </div>
                        
                        <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                          <Text variant="headingXl" as="h3">
                            ${price}<Text variant="bodyMd" as="span">/{billingPeriod === 'monthly' ? 'month' : 'year'}</Text>
                          </Text>
                          {/* Show crossed-out original price if there's an active subscription sale */}
                          {activeSales.subscription.active && (
                            <div style={{ marginTop: '0.25rem' }}>
                              <Text variant="bodyMd" as="p" color="subdued">
                                <s>${originalPrice}</s> <Text variant="bodyMd" as="span" color="critical">{activeSales.subscription.percentage}% off</Text>
                              </Text>
                            </div>
                          )}
                          {savingsText && (
                            <div style={{ marginTop: '5px' }}>
                              <Badge status="success">{savingsText}</Badge>
                            </div>
                          )}
                          <div style={{ marginTop: '10px', color: '#637381' }}>
                            <Text variant="bodyMd">{plan.monthlyCredits} credits/{billingPeriod === 'monthly' ? 'month' : 'year'}</Text>
                          </div>
                          <div style={{ marginTop: '10px' }}>
                            <Badge status="info">Edit up to {plan.maxProductSelection} products at once</Badge>
                          </div>
                          {/* Product count with info tooltip */}
                          <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                              <Text variant="bodyMd" as="span">
                                Up to {plan.maxProductSelection} product edits/month
                              </Text>
                              <button 
                                onClick={() => handleShowExplanation(`We calculate product edits based on generating core selling content like:
• Product title
• Description
• Basic specifications

This uses ~7 credits per product on average.

🔧 Using advanced features like:
• Benefits, SEO keywords
• Option name translation
• Image conversion
…may require more credits per product (up to 20).`)}
                                style={{ 
                                  background: 'none', 
                                  border: 'none', 
                                  cursor: 'pointer', 
                                  fontSize: '16px', 
                                  marginLeft: '4px',
                                  color: '#637381',
                                  padding: '0',
                                  display: 'inline-flex',
                                  alignItems: 'center'
                                }}
                                aria-label="View product edits explanation"
                              >
                                &#9432;
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '20px', flex: 1 }}>
                          {paddedFeatures.map((feature, index) => (
                            <div key={index} style={{ 
                              marginBottom: '12px', 
                              display: 'flex', 
                              alignItems: 'flex-start',
                              paddingLeft: '20px',
                              paddingRight: '20px'
                            }}>
                              {feature ? (
                                <>
                                  <div style={{ 
                                    minWidth: '24px', 
                                    marginRight: '10px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center' 
                                  }}>
                                    <Icon source={CircleTickMajor} color="success" />
                                  </div>
                                  <div style={{ textAlign: 'left', flex: 1 }}>{feature}</div>
                                </>
                              ) : <div style={{ height: '20px' }} />}
                            </div>
                          ))}
                        </div>
                        
                        <div style={{ marginTop: 'auto' }}>
                          <Button
                            primary={isMostPopular}
                            fullWidth
                            onClick={handleLoginClick}
                          >
                            Sign Up Now
                          </Button>
                        </div>
                      </Card>
                    </div>
                  </Layout.Section>
                );
              })
            )
          )}
        </Layout>
        
        {/* Flex Credits Section */}
        <div style={{ marginTop: '60px' }}>
          {/* Active Sale Banner for Flex Credits */}
          {activeSales.flexCredits.active && (
            <div style={{ marginBottom: '20px' }}>
              <Banner status="success" title={activeSales.flexCredits.name || 'Limited Time Offer'}>
                <p>Save {activeSales.flexCredits.percentage}% on all flex credit packages!</p>
              </Banner>
            </div>
          )}

          <Heading element="h2">Flex Credits</Heading>
          <Text variant="bodyLg" as="p" color="subdued" style={{ marginBottom: '25px' }}>
            Purchase additional credits anytime without changing your plan
          </Text>
        </div>
        
        <Layout>
          {/* Small Boost */}
          <Layout.Section oneThird>
            <div style={{ height: '100%' }}>
              <Card 
                sectioned 
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 128, 96, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: '15px', minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                    {renderStarsForPackage(1)}
                  </div>
                  <Heading element="h2">Small Boost</Heading>
                  <div style={{ height: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}></div>
                  <div style={{ marginTop: '0.25rem', minHeight: '30px' }}>
                    <TextStyle variation="subdued">Perfect for quick fixes</TextStyle>
                  </div>
                  <div style={{ margin: '1rem 0' }}>
                    <Text variant="headingXl" as="p">$7.99</Text>
                    {activeSales.flexCredits.active && (
                      <div style={{ marginTop: '0.25rem' }}>
                        <Text variant="bodyMd" as="p" color="subdued">
                          <s>$15.99</s> <Text variant="bodyMd" as="span" color="critical">{activeSales.flexCredits.percentage}% off</Text>
                        </Text>
                      </div>
                    )}
                  </div>
                  <div style={{ margin: '0.5rem 0', height: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Badge status="info">100 Credits</Badge>
                  </div>
                  <div style={{ marginTop: '0.5rem', textAlign: 'center' }}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <p style={{margin: 0, padding: 0, fontSize: '14px'}}>Up to 14 product edits</p>
                      <button
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontSize: '16px', 
                          marginLeft: '4px',
                          color: '#637381',
                          padding: '0',
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}
                        onClick={() => handleShowExplanation(`We calculate product edits based on generating core selling content like:
• Product title
• Description
• Basic specifications

This uses ~7 credits per product on average.

🔧 Using advanced features like:
• Benefits, SEO keywords
• Option name translation
• Image conversion
…may require more credits per product (up to 20).`)}
                        aria-label="View product edits explanation"
                      >
                        &#9432;
                      </button>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '20px', flex: 1 }}>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>One-time purchase</div>
                  </div>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>Never expires</div>
                  </div>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>Use for any product edits</div>
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: 'auto', paddingTop: '1rem' }}>
                  <Button onClick={handleLoginClick} fullWidth>Sign Up to Purchase</Button>
                </div>
              </Card>
            </div>
          </Layout.Section>

          {/* Medium Boost */}
          <Layout.Section oneThird>
            <div style={{ height: '100%' }}>
              <Card 
                sectioned 
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 128, 96, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: '15px', minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                    {renderStarsForPackage(2)}
                  </div>
                  <Heading element="h2">Medium Boost</Heading>
                  <div style={{ height: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}></div>
                  <div style={{ marginTop: '0.25rem', minHeight: '30px' }}>
                    <TextStyle variation="subdued">Ideal for flash updates or new arrivals</TextStyle>
                  </div>
                  <div style={{ margin: '1rem 0' }}>
                    <Text variant="headingXl" as="p">$17.99</Text>
                    {activeSales.flexCredits.active && (
                      <div style={{ marginTop: '0.25rem' }}>
                        <Text variant="bodyMd" as="p" color="subdued">
                          <s>$35.99</s> <Text variant="bodyMd" as="span" color="critical">{activeSales.flexCredits.percentage}% off</Text>
                        </Text>
                      </div>
                    )}
                  </div>
                  <div style={{ margin: '0.5rem 0', height: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Badge status="info">250 Credits</Badge>
                  </div>
                  <div style={{ marginTop: '0.5rem', textAlign: 'center' }}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <p style={{margin: 0, padding: 0, fontSize: '14px'}}>Up to 35 product edits</p>
                      <button
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontSize: '16px', 
                          marginLeft: '4px',
                          color: '#637381',
                          padding: '0',
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}
                        onClick={() => handleShowExplanation(`We calculate product edits based on generating core selling content like:
• Product title
• Description
• Basic specifications

This uses ~7 credits per product on average.

🔧 Using advanced features like:
• Benefits, SEO keywords
• Option name translation
• Image conversion
…may require more credits per product (up to 20).`)}
                        aria-label="View product edits explanation"
                      >
                        &#9432;
                      </button>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '20px', flex: 1 }}>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>One-time purchase</div>
                  </div>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>Never expires</div>
                  </div>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>Use for any product edits</div>
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: 'auto', paddingTop: '1rem' }}>
                  <Button onClick={handleLoginClick} fullWidth>Sign Up to Purchase</Button>
                </div>
              </Card>
            </div>
          </Layout.Section>
          
          {/* Large Boost */}
          <Layout.Section oneThird>
            <div style={{ height: '100%' }}>
              <Card 
                sectioned 
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: '0 0 10px rgba(0, 128, 96, 0.3)',
                  border: '2px solid #008060',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 128, 96, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 0 10px rgba(0, 128, 96, 0.3)';
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: '15px', minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                    {renderStarsForPackage(3)}
                  </div>
                  <Heading element="h2">Large Boost</Heading>
                  <div style={{ height: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Badge status="success">BEST VALUE</Badge>
                  </div>
                  <div style={{ marginTop: '0.25rem', minHeight: '30px' }}>
                    <TextStyle variation="subdued">Scale faster during seasonal pushes</TextStyle>
                  </div>
                  <div style={{ margin: '1rem 0' }}>
                    <Text variant="headingXl" as="p">$39.99</Text>
                    {activeSales.flexCredits.active && (
                      <div style={{ marginTop: '0.25rem' }}>
                        <Text variant="bodyMd" as="p" color="subdued">
                          <s>$79.99</s> <Text variant="bodyMd" as="span" color="critical">{activeSales.flexCredits.percentage}% off</Text>
                        </Text>
                      </div>
                    )}
                  </div>
                  <div style={{ margin: '0.5rem 0', height: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Badge status="info">600 Credits</Badge>
                  </div>
                  <div style={{ marginTop: '0.5rem', textAlign: 'center' }}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <p style={{margin: 0, padding: 0, fontSize: '14px'}}>Up to 75 product edits</p>
                      <button
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontSize: '16px', 
                          marginLeft: '4px',
                          color: '#637381',
                          padding: '0',
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}
                        onClick={() => handleShowExplanation(`We calculate product edits based on generating core selling content like:
• Product title
• Description
• Basic specifications

This uses ~7 credits per product on average.

🔧 Using advanced features like:
• Benefits, SEO keywords
• Option name translation
• Image conversion
…may require more credits per product (up to 20).`)}
                        aria-label="View product edits explanation"
                      >
                        &#9432;
                      </button>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '20px', flex: 1 }}>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>One-time purchase</div>
                  </div>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>Never expires</div>
                  </div>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>Use for any product edits</div>
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: 'auto', paddingTop: '1rem' }}>
                  <Button primary onClick={handleLoginClick} fullWidth>Sign Up to Purchase</Button>
                </div>
              </Card>
            </div>
          </Layout.Section>

          {/* XL Boost */}
          <Layout.Section oneThird>
            <div style={{ height: '100%' }}>
              <Card 
                sectioned 
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 128, 96, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: '15px', minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                    {renderStarsForPackage(4)}
                  </div>
                  <Heading element="h2">XL Boost</Heading>
                  <div style={{ height: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}></div>
                  <div style={{ marginTop: '0.25rem', minHeight: '30px' }}>
                    <TextStyle variation="subdued">Great for monthly syncs or content sprints</TextStyle>
                  </div>
                  <div style={{ margin: '1rem 0' }}>
                    <Text variant="headingXl" as="p">$74.99</Text>
                    {activeSales.flexCredits.active && (
                      <div style={{ marginTop: '0.25rem' }}>
                        <Text variant="bodyMd" as="p" color="subdued">
                          <s>$149.99</s> <Text variant="bodyMd" as="span" color="critical">{activeSales.flexCredits.percentage}% off</Text>
                        </Text>
                      </div>
                    )}
                  </div>
                  <div style={{ margin: '0.5rem 0', height: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Badge status="info">1250 Credits</Badge>
                  </div>
                  <div style={{ marginTop: '0.5rem', textAlign: 'center' }}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <p style={{margin: 0, padding: 0, fontSize: '14px'}}>Up to 175 product edits</p>
                      <button
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontSize: '16px', 
                          marginLeft: '4px',
                          color: '#637381',
                          padding: '0',
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}
                        onClick={() => handleShowExplanation(`We calculate product edits based on generating core selling content like:
• Product title
• Description
• Basic specifications

This uses ~7 credits per product on average.

🔧 Using advanced features like:
• Benefits, SEO keywords
• Option name translation
• Image conversion
…may require more credits per product (up to 20).`)}
                        aria-label="View product edits explanation"
                      >
                        &#9432;
                      </button>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '20px', flex: 1 }}>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>One-time purchase</div>
                  </div>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>Never expires</div>
                  </div>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>Use for any product edits</div>
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: 'auto', paddingTop: '1rem' }}>
                  <Button primary onClick={handleLoginClick} fullWidth>Sign Up to Purchase</Button>
                </div>
              </Card>
            </div>
          </Layout.Section>

          {/* XXL Boost */}
          <Layout.Section oneThird>
            <div style={{ height: '100%' }}>
              <Card 
                sectioned 
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 128, 96, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: '15px', minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                    {renderStarsForPackage(5)}
                  </div>
                  <Heading element="h2">XXL Boost</Heading>
                  <div style={{ height: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}></div>
                  <div style={{ marginTop: '0.25rem', minHeight: '30px' }}>
                    <TextStyle variation="subdued">Built for large dropshipping and agency-scale needs</TextStyle>
                  </div>
                  <div style={{ margin: '1rem 0' }}>
                    <Text variant="headingXl" as="p">$149.99</Text>
                    {activeSales.flexCredits.active && (
                      <div style={{ marginTop: '0.25rem' }}>
                        <Text variant="bodyMd" as="p" color="subdued">
                          <s>$299.99</s> <Text variant="bodyMd" as="span" color="critical">{activeSales.flexCredits.percentage}% off</Text>
                        </Text>
                      </div>
                    )}
                  </div>
                  <div style={{ margin: '0.5rem 0', height: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Badge status="info">2500 Credits</Badge>
                  </div>
                  <div style={{ marginTop: '0.5rem', textAlign: 'center' }}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <p style={{margin: 0, padding: 0, fontSize: '14px'}}>Up to 350 product edits</p>
                      <button
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontSize: '16px', 
                          marginLeft: '4px',
                          color: '#637381',
                          padding: '0',
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}
                        onClick={() => handleShowExplanation(`We calculate product edits based on generating core selling content like:
• Product title
• Description
• Basic specifications

This uses ~7 credits per product on average.

🔧 Using advanced features like:
• Benefits, SEO keywords
• Option name translation
• Image conversion
…may require more credits per product (up to 20).`)}
                        aria-label="View product edits explanation"
                      >
                        &#9432;
                      </button>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '20px', flex: 1 }}>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>One-time purchase</div>
                  </div>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>Never expires</div>
                  </div>
                  <div style={{ 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                  }}>
                    <div style={{ 
                      minWidth: '24px', 
                      marginRight: '10px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Icon source={CircleTickMajor} color="success" />
                    </div>
                    <div style={{ textAlign: 'left', flex: 1 }}>Use for any product edits</div>
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: 'auto', paddingTop: '1rem' }}>
                  <Button primary onClick={handleLoginClick} fullWidth>Sign Up to Purchase</Button>
                </div>
              </Card>
            </div>
          </Layout.Section>
        </Layout>

        {/* FAQ Section */}
        <div style={{ marginTop: '40px', marginBottom: '60px' }}>
          <Heading element="h2">Frequently Asked Questions</Heading>
          
          <div style={{ marginTop: '30px' }}>
            <Card>
              <Card.Section>
                <Heading element="h3">What are Flex Credits?</Heading>
                <Text variant="bodyMd" as="p" style={{ marginTop: '10px' }}>
                  Flex Credits are used for AI-powered content generation. Each product edit uses credits based on the amount of content generated, with an average of 7 credits per basic product edit. They provide flexibility to process additional products beyond your monthly subscription allowance.
                </Text>
              </Card.Section>
              
              <Card.Section>
                <Heading element="h3">How many credits do I need?</Heading>
                <Text variant="bodyMd" as="p" style={{ marginTop: '10px' }}>
                  Basic product edits typically use about 7 credits, while more comprehensive changes may require up to 20 credits per product. If you have 100 products to optimize, you can expect to use approximately 700-1000 credits depending on the level of detail in your content generation.
                </Text>
              </Card.Section>
              
              <Card.Section>
                <Heading element="h3">What happens if I run out of credits?</Heading>
                <Text variant="bodyMd" as="p" style={{ marginTop: '10px' }}>
                  When you run out of monthly credits, you can purchase additional Flex Credits without changing your subscription plan. Your subscription credits are automatically renewed at the start of each billing cycle.
                </Text>
              </Card.Section>
              
              <Card.Section>
                <Heading element="h3">Can I change plans later?</Heading>
                <Text variant="bodyMd" as="p" style={{ marginTop: '10px' }}>
                  Yes, you can upgrade or downgrade your subscription plan at any time. When upgrading, you'll immediately receive the benefits of your new plan. When downgrading, the change will take effect at the start of your next billing cycle.
                </Text>
              </Card.Section>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div style={{ 
          marginTop: '40px', 
          padding: '40px 20px', 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #f9f9ff 0%, #f0f9ff 100%)',
          borderRadius: '8px'
        }}>
          <Heading>Ready to Optimize Your Product Listings?</Heading>
          <Text as="p" style={{ margin: '15px auto 30px', maxWidth: '650px' }}>
            Start with a free trial. No credit card required.
          </Text>
          <Button size="large" primary onClick={handleLoginClick}>
            Start Free Trial
          </Button>
        </div>

        {renderExplanationModal}
      </Page>
    </Frame>
  );
};

export default Pricing;
